/**
 * Canonical event data for the 2 August 2027 total solar eclipse.
 *
 * All instants are stored as UTC ISO strings — the single source of truth.
 * Anything local (Makkah AST = UTC+3, the visitor's own zone) is derived at
 * render time, never stored. Do not hand-edit these into local time.
 */

export type EclipseContactKey =
  | 'partialBegin'
  | 'totalityBegin'
  | 'maximum'
  | 'totalityEnd'
  | 'partialEnd'

export interface EclipseContacts {
  /** C1 — the Moon first touches the solar disc. */
  partialBegin: string
  /** C2 — totality begins. */
  totalityBegin: string
  /** Greatest eclipse for this location. */
  maximum: string
  /** C3 — totality ends. */
  totalityEnd: string
  /** C4 — the Moon leaves the solar disc. */
  partialEnd: string
}

export interface ObservationSite {
  id: string
  city: string
  country: { id: string; en: string }
  /** IANA time zone, used for rendering local contact times. */
  timeZone: string
  coordinates: { lat: number; lon: number }
  /** Totality length in seconds. */
  totalitySeconds: number
  /** Sun altitude above the horizon at maximum, in degrees. */
  sunAltitudeDeg: number
  /**
   * `verified` = cross-checked against the project's ephemeris reference.
   * `draft`    = planning estimate; must be verified before publication.
   */
  dataStatus: 'verified' | 'draft'
  /** Is this a site the expedition team intends to staff? */
  isPrimary?: boolean
}

/** The eclipse date, as a plain calendar date. */
export const ECLIPSE_DATE_ISO = '2027-08-02'

/**
 * Makkah contact times (AST, UTC+3) from the project PRD:
 *   partial 12:01:00 · totality 13:23:48 → 13:28:53 · max 13:26:20 · partial ends 14:44:00
 */
export const MAKKAH_CONTACTS: EclipseContacts = {
  partialBegin: '2027-08-02T09:01:00Z',
  totalityBegin: '2027-08-02T10:23:48Z',
  maximum: '2027-08-02T10:26:20Z',
  totalityEnd: '2027-08-02T10:28:53Z',
  partialEnd: '2027-08-02T11:44:00Z',
}

/** The instant the whole site counts down to. */
export const COUNTDOWN_TARGET = MAKKAH_CONTACTS.totalityBegin

export const MAKKAH_TIME_ZONE = 'Asia/Riyadh'

/**
 * Candidate observation sites along the path of totality.
 *
 * TODO(content): every entry except Makkah is a planning estimate carrying
 * `dataStatus: 'draft'`. Verify against NASA GSFC / Besselian elements before
 * these numbers are presented as official, and flip the flag.
 */
export const OBSERVATION_SITES: ObservationSite[] = [
  {
    id: 'makkah',
    city: 'Makkah',
    country: { id: 'Arab Saudi', en: 'Saudi Arabia' },
    timeZone: 'Asia/Riyadh',
    coordinates: { lat: 21.4225, lon: 39.8262 },
    totalitySeconds: 305,
    sunAltitudeDeg: 68,
    dataStatus: 'verified',
    isPrimary: true,
  },
  {
    id: 'luxor',
    city: 'Luxor',
    country: { id: 'Mesir', en: 'Egypt' },
    timeZone: 'Africa/Cairo',
    coordinates: { lat: 25.6872, lon: 32.6396 },
    totalitySeconds: 383,
    sunAltitudeDeg: 82,
    dataStatus: 'draft',
    isPrimary: true,
  },
  {
    id: 'benghazi',
    city: 'Benghazi',
    country: { id: 'Libya', en: 'Libya' },
    timeZone: 'Africa/Tripoli',
    coordinates: { lat: 32.1167, lon: 20.0667 },
    totalitySeconds: 290,
    sunAltitudeDeg: 68,
    dataStatus: 'draft',
  },
  {
    id: 'sfax',
    city: 'Sfax',
    country: { id: 'Tunisia', en: 'Tunisia' },
    timeZone: 'Africa/Tunis',
    coordinates: { lat: 34.7406, lon: 10.7603 },
    totalitySeconds: 230,
    sunAltitudeDeg: 60,
    dataStatus: 'draft',
  },
  {
    id: 'tangier',
    city: 'Tangier',
    country: { id: 'Maroko', en: 'Morocco' },
    timeZone: 'Africa/Casablanca',
    coordinates: { lat: 35.7595, lon: -5.834 },
    totalitySeconds: 160,
    sunAltitudeDeg: 45,
    dataStatus: 'draft',
  },
]

export const PRIMARY_SITE = OBSERVATION_SITES[0]!

/** Contact keys in chronological order, for timeline rendering. */
export const CONTACT_ORDER: EclipseContactKey[] = [
  'partialBegin',
  'totalityBegin',
  'maximum',
  'totalityEnd',
  'partialEnd',
]
